<template>
  <div class="login-page">
    <!-- 背景装饰：渐变光斑 + 网格 -->
    <div class="login-bg" aria-hidden="true">
      <span class="blob blob-a"></span>
      <span class="blob blob-b"></span>
      <span class="blob blob-c"></span>
      <span class="grid-mask"></span>
    </div>

    <div class="login-body">
      <!-- 左侧：登录卡片 -->
      <section class="login-form-box" :class="{ 'is-shake': shakeForm }">
        <header class="login-form-box-header">
          <div class="logo">
            <img src="@/assets/img/v2/navbar/logo.png" class="logo-hrcloud" alt="logo" />
            <span class="logo-line"></span>
            <img src="@/assets/img/main/logo-payroll-tax.png" class="logo-payroll-tax" alt="payroll" />
          </div>
          <el-dropdown @command="handleCommand" placement="bottom-end" trigger="click">
            <span class="lang-switch-trigger">
              {{ lang === 'zh' ? '中文' : 'English' }}<i class="el-icon-caret-bottom"></i>
            </span>
            <el-dropdown-menu slot="dropdown">
              <el-dropdown-item :command="item.value" v-for="item in language" :key="item.value">
                {{ item.label }}
              </el-dropdown-item>
            </el-dropdown-menu>
          </el-dropdown>
        </header>

        <transition name="view-fade" mode="out-in">
          <!-- ========== 第一步：账号密码 ========== -->
          <div v-if="step === 1" key="login" class="login-form">
            <h1 class="login-form-title">{{ getLoginFormTitle }}</h1>
            <p class="login-form-subtitle">{{ getSubTitle }}</p>

            <el-form
              ref="loginForm"
              :model="loginForm"
              :rules="loginRules"
              hide-required-asterisk
              @submit.native.prevent
            >
              <el-form-item prop="username" class="field">
                <el-input
                  v-model.trim="loginForm.username"
                  size="medium"
                  prefix-icon="el-icon-user"
                  autocomplete="username"
                  clearable
                  :placeholder="$t('logon.iptAccount')"
                  @keyup.enter.native="handleLogin"
                />
              </el-form-item>

              <el-form-item prop="password" class="field">
                <el-input
                  v-model="loginForm.password"
                  size="medium"
                  :type="passwordType"
                  prefix-icon="el-icon-lock"
                  autocomplete="current-password"
                  :placeholder="$t('logon.iptPassword')"
                  @keyup.enter.native="handleLogin"
                >
                  <i
                    slot="suffix"
                    class="pwd-toggle el-icon-view"
                    :class="{ 'is-off': !pwdVisible }"
                    @click="showPassword"
                  ></i>
                </el-input>
              </el-form-item>

              <el-form-item prop="code" class="field" v-show="loginForm.loginType === '2'">
                <div class="code-row">
                  <el-input
                    v-model.trim="loginForm.code"
                    size="medium"
                    maxlength="4"
                    autocomplete="off"
                    :placeholder="$t('logon.iptCheckcode')"
                    @keyup.enter.native="handleLogin"
                  />
                  <div class="code-img" :class="{ 'is-loading': codeLoading }" title="点击刷新" @click="refreshCode">
                    <img v-if="codeImg.src" :src="codeImg.src" alt="verify code"
                         @load="codeLoading = false" @error="codeLoading = false" />
                    <i v-if="codeLoading" class="el-icon-loading"></i>
                  </div>
                </div>
              </el-form-item>

              <div class="login-options">
                <el-checkbox v-model="rememberMe">{{ lang === 'zh' ? '记住账号' : 'Remember me' }}</el-checkbox>
                <div class="links">
                  <a @click="handleReister">{{ $t('logon.register') }}</a>
                  <span class="divider"></span>
                  <a @click="handleResetPwd">{{ $t('logon.forgetPassword') }}</a>
                </div>
              </div>

              <el-button
                class="login-submit"
                :loading="loginLoading"
                @click.native.prevent="handleLogin"
              >
                {{ loginLoading ? (lang === 'zh' ? '登录中…' : 'Signing in…') : $t('logon.login') }}
              </el-button>

              <div class="agreement">
                <el-checkbox v-model="checked"></el-checkbox>
                <span class="agreement-text">
                  {{ $t('logon.agree') }}
                  <a
                    class="policy-link"
                    href="https://hrcloud.ciicpt.com.cn/ppolicy.html"
                    target="_blank"
                    rel="noopener"
                  >{{ $t('logon.privacyPolicy') }}</a>
                </span>
              </div>
            </el-form>
          </div>

          <!-- ========== 第二步：选择企业 ========== -->
          <div
            v-else
            key="enterprise"
            class="enterprise-panel"
            v-loading="loginLoading"
            :element-loading-text="lang === 'zh' ? '正在进入…' : 'Loading…'"
          >
            <h1 class="login-form-title">{{ getLoginFormTitle }}</h1>
            <p class="login-form-subtitle">
              {{ lang === 'zh' ? '检测到你的账号关联了多个企业，请选择要进入的企业' : 'Multiple enterprises found, please choose one' }}
            </p>
            <ul class="enterprise-list">
              <li
                class="enterprise-item"
                v-for="(item, index) in enterpriseList"
                :key="item.CUSID"
                :style="{ animationDelay: index * 60 + 'ms' }"
                @click="handleSwitchCus(item)"
              >
                <span class="enterprise-avatar">{{ (item.CUSNAME || '?').charAt(0) }}</span>
                <span class="enterprise-name">{{ item.CUSNAME }}</span>
                <i class="el-icon-arrow-right"></i>
              </li>
            </ul>
            <a class="back-link" @click="backToLogin">
              <i class="el-icon-back"></i> {{ lang === 'zh' ? '返回登录' : 'Back' }}
            </a>
          </div>
        </transition>
      </section>

      <!-- 右侧：品牌展示 -->
      <section class="login-desc-box">
        <main class="login-desc-main">
          <div class="sys-name-row">
            <div class="sys-name">
              <el-image
                style="height: 100%; width: auto;"
                :src="require(`@/assets/img/v2/login/payroll-service-platform-${lang}.png`)"
                fit="scale-down"
              ></el-image>
            </div>

            <div class="login-version">
              <el-tag size="small" effect="plain" class="version-tag">
                <i class="version-dot"></i>
                <span class="version-text">v6.0</span>
              </el-tag>
            </div>
          </div>
          <div class="payroll-text">
            <el-image
              style="width: 100%;"
              :src="require(`@/assets/img/v2/login/payroll-text-${lang}.png`)"
              fit="scale-down"
            ></el-image>
          </div>
          <a class="about-us" href="http://www.ciicpt.com.cn/" target="_blank" rel="noopener">
            <el-image
              style="width: 100%; height: 100%;"
              :src="require(`@/assets/img/v2/login/aboutus-${lang}.png`)"
              fit="scale-down"
            ></el-image>
          </a>
        </main>
        <footer class="f-copyright">{{ $t('logon.copyRight') }}</footer>
      </section>
    </div>

    <!-- 修改密码 -->
    <update-password ref="updatePassword" @password-closed="updatePasswordClosed"></update-password>
  </div>
</template>

<script>
import UpdatePassword from '@/page/login-v2/updatePassword';
import { mapGetters } from 'vuex';
import {guid, encrypt, randomLenNum} from '@/util/util';
import { base64Encode } from '@/util/endecode';
import { setRem } from '@/util/commen';
import hrCommon from '@/api/common/hrcloudCommon';
import { switchCus } from '@/api/user/login';

export default {
  name: 'LoginV2',
  components: { UpdatePassword },
  data() {
    return {
      codeImg: { src: '', len: 4 },
      codeLoading: false,
      language: [
        { value: 'zh', label: '中文' },
        { value: 'en', label: 'English' }
      ],
      checked: false,
      rememberMe: true,
      pwdVisible: false,
      passwordType: 'password',
      loginForm: {
        username: '',
        password: '',
        cusId: '',
        branchId: '',
        code: '',
        randomStr: '',
        loginType: '1',
        codeKey: ''
      },
      loginRules: {
        username: [{ required: true, message: this.$t('logon.iptAccount'), trigger: 'blur' }],
        password: [
          { required: true, message: this.$t('logon.iptPassword'), trigger: 'blur' },
          { min: 6, message: this.$t('logon.iptPasswordLen'), trigger: 'blur' }
        ]
      },
      loginLoading: false,
      step: 1,
      enterpriseList: [],
      shakeForm: false
    };
  },
  watch: {
    $route(val) {
      this.step = this.accessToken ? Number(val.query.step || 1) : 1;
      this.loginLoading = false;
    }
  },
  created() {
    setRem();
    localStorage.setItem('version', this.$route.meta.version);

    const lang = localStorage.getItem('lang');
    if (lang) {
      this.$i18n.locale = lang;
    }

    // 记住账号回填
    const savedName = localStorage.getItem('login_username');
    if (savedName) {
      this.loginForm.username = savedName;
      this.rememberMe = true;
    }
  },
  methods: {
    /* ---------------- 语言 ---------------- */
    handleCommand(command) {
      if (this.$i18n.locale !== command) {
        this.changeLaguages(command);
      }
    },
    changeLaguages(lang) {
      this.$i18n.locale = lang;
      localStorage.setItem('lang', lang);
      this.$store.commit('CHANGE_LANG', lang);
      this.loginRules = {
        username: [{ required: true, message: this.$t('logon.iptAccount'), trigger: 'blur' }],
        password: [
          { required: true, message: this.$t('logon.iptPassword'), trigger: 'blur' },
          { min: 6, message: this.$t('logon.iptPasswordLen'), trigger: 'blur' }
        ]
      };
    },

    /* ---------------- 验证码 ---------------- */
    refreshCode() {
      this.loginForm.code = '';
      this.loginForm.codeKey = guid();
      this.loginForm.randomStr = randomLenNum(this.codeImg.len, true);
      this.codeImg.type == 'text'
        ? (this.codeImg.value = randomLenNum(this.codeImg.len))
        : (this.codeImg.src = `${hrCommon.HRCLOUD_URL}util/getVerifyCodeNew.action?codeKey=` + base64Encode(this.loginForm.codeKey)); //调用人事云接口获取 验证码
    },
    // 登录失败后自动开启验证码
    enableCode() {
      if (this.loginForm.loginType !== '2') {
        this.loginForm.loginType = '2';
        this.loginRules.code = [
          { required: true, message: this.$t('logon.iptCheckcode'), trigger: 'blur' },
          { min: 4, max: 4, message: this.$t('logon.iptCheckcodeLen'), trigger: 'blur' }
        ];
        this.$nextTick(this.refreshCode);
      } else {
        this.refreshCode();
      }
    },

    /* ---------------- 密码可见 ---------------- */
    showPassword() {
      this.pwdVisible = !this.pwdVisible;
      this.passwordType = this.pwdVisible ? '' : 'password';
    },

    /* ---------------- 登录 ---------------- */
    handleLogin() {
      if (this.loginLoading) return;

      this.$refs.loginForm.validate((valid) => {
        if (!valid) {
          this.playShake();
          return;
        }
        if (!this.checked) {
          this.playShake();
          this.$message.warning(this.$t('logon.agreeMsg'));
          return;
        }
        this.doLogin();
      });
    },

    playShake() {
      this.shakeForm = false;
      this.$nextTick(() => {
        this.shakeForm = true;
        setTimeout(() => (this.shakeForm = false), 520);
      });
    },

    doLogin() {
      const username = this.loginForm.username;
      if (/^\d+$/.test(username)) {
        this.loginForm.type = 2; // 手机号
      } else if (username.indexOf('@') !== -1) {
        this.loginForm.type = 3; // 邮箱
      } else {
        this.loginForm.type = 1; // 用户名
      }

      this.loginLoading = true;
      this.$store
        .dispatch('LoginByUsername', this.loginForm)
        .then((res) => {
          if (!res.data.success) {
            this.loginLoading = false;
            this.$message.error(res.data.msg);
            this.enableCode();
            return;
          }
          this.persistAccount();

          const { isInitPwd, isUpdatePwdTips } = res.data.data;
          if (isUpdatePwdTips) {
            this.$alert(this.$t('logon.passwordTipContent'), this.$t('logon.passwordTipTitle'), {
              confirmButtonText: this.$t('determine'),
              callback: (action) => this.alertCallback(action)
            });
          } else if (isInitPwd) {
            this.$alert(this.$t('logon.updatePwdTipContent'), this.$t('logon.passwordTipTitle'), {
              confirmButtonText: this.$t('determine'),
              callback: (action) => this.alertCallback(action)
            });
          } else {
            this.loginCallback(res);
          }
        })
        .catch((error) => {
          this.loginLoading = false;
          if (error.data && error.data.msg) {
            this.$message.error(error.data.msg);
          }
          this.enableCode();
        });
    },

    persistAccount() {
      if (this.rememberMe) {
        localStorage.setItem('login_username', this.loginForm.username);
      } else {
        localStorage.removeItem('login_username');
      }
    },

    /* ---------------- 登录成功 ---------------- */
    loginCallback(res) {
      const { cusList } = res.data.data;
      if (cusList && cusList.length > 1) {
        this.enterpriseList = cusList;
        this.step = 2;
        this.loginLoading = false;
        this.$router.push({ path: '/login', query: { step: 2 } });
        setTimeout(() => {
          this.$store.commit('SET_ACCESS_TOKEN', '');
        }, 200);
      } else {
        this.redirectPath(res);
      }
    },

    handleSwitchCus(cus) {
      this.loginLoading = true;
      const { CUSID, BRANCHID, CUSNAME, cusLogoUrl } = cus;
      const { access_token } = this.userInfo;

      this.$store.commit('SET_CUS_LOGO', cusLogoUrl);
      this.$store.commit('SET_ACCESS_TOKEN', access_token);

      const params = { cusId: CUSID, branchId: BRANCHID, cusName: CUSNAME };
      switchCus(params)
        .then((res) => {
          this.loginLoading = false;
          const resData = res.data.data;
          if (res.data.statusCode == 200 && res.data.success) {
            this.$message.success(res.data.msg);
            this.$store.commit('SET_ACCESS_TOKEN', resData.access_token);
            this.$store.commit('SET_REFRESH_TOKEN', resData.refresh_token);
            this.$store.commit('SET_CUS_ID', resData.cusId);
            this.$store.commit('SET_USER_INFO', resData);
            this.$store.commit('SET_BRANCH_INFO', { id: resData.branchId });
            this.$store.commit('SET_PMS', resData.pms);
            this.$store.commit('SET_AUTHORIZED_AGENCIES', resData.authorizedAgencies);
            this.$store.commit('SET_CUS_LIST', resData.cusList);
            this.$store.commit('CLEAR_LOCK');
            this.redirectPath(res);
          } else {
            this.$store.commit('SET_ACCESS_TOKEN', '');
            this.$message.error(res.data.msg);
          }
        })
        .catch(() => {
          this.$store.commit('SET_ACCESS_TOKEN', '');
          this.loginLoading = false;
        });
    },

    async redirectPath(res) {
      const { userType, cusSourceType, cusId } = res.data.data;

      if (userType != '10620003') {
        // HR
        if (cusSourceType == '1') {
          if (cusId == '1019672113180573696') {
            this.$router.push({ path: '/nbqx/index.action' });
          } else {
            const pmsList = (this.pms && this.pms[1]) || [];
            const hasZLPermission = pmsList.some((item) => item.operateCode == 'hr_wage_new_report_data_center_zl');
            this.$router.push({ path: hasZLPermission ? '/v2/stats/data-center-dashboard' : '/index' });
          }
          this.$message.success('登录成功');
        } else {
          const hrUrl =
            hrCommon.HRCLOUD_URL +
            'secondMain.action?accessToken=' +
            base64Encode(encrypt(this.accessToken, ''));
          window.location.href = hrUrl;
        }
      } else {
        // 雇员
        const empUrl =
          hrCommon.HRCLOUD_URL +
          'emp/index.action?accessToken=' +
          base64Encode(encrypt(this.accessToken, ''));
        window.location.href = empUrl;
      }
    },

    backToLogin() {
      this.step = 1;
      this.loginLoading = false;
      this.enterpriseList = [];
      this.$router.replace({ path: '/login', query: {} });
      this.updatePasswordClosed();
    },

    /* ---------------- 跳转链接 ---------------- */
    handleResetPwd() {
      const lang = localStorage.getItem('lang');
      const param = lang ? '?lang=' + base64Encode(lang) : '';
      window.open(hrCommon.HRCLOUD_URL + 'accountConfirmation.action' + param, '_blank', 'noopener');
    },
    handleReister() {
      const lang = localStorage.getItem('lang');
      const param = lang ? '?lang=' + base64Encode(lang) : '';
      window.open(hrCommon.HRCLOUD_URL + 'toRegister.action' + param, '_blank', 'noopener');
    },

    /* ---------------- 修改密码 ---------------- */
    alertCallback(action) {
      if (action == 'cancel') {
        this.updatePasswordClosed();
      } else {
        this.$refs.updatePassword.visible = true;
      }
    },
    updatePasswordClosed() {
      this.checked = false;
      this.loginLoading = false;
      this.$store.dispatch('ClearCache');
    }
  },
  computed: {
    ...mapGetters(['lang', 'userInfo', 'accessToken', 'pms']),
    getLoginFormTitle() {
      return this.step === 1 ? this.$t('logon.passwordLogin') : this.$t('logon.selectEnterprise');
    },
    getSubTitle() {
      return this.lang === 'zh' ? '欢迎回来，请登录你的账号' : 'Welcome back, please sign in';
    }
  }
};
</script>

<style scoped>
/* ================= 页面骨架 ================= */
.login-page {
  position: relative;
  min-height: 100vh;
  display: flex;
  overflow: hidden;
  background: #f4f6fb;
  font-family: -apple-system, BlinkMacSystemFont, 'PingFang SC', 'Microsoft YaHei', sans-serif;
}

/* ================= 背景装饰 ================= */
.login-bg {
  position: absolute;
  inset: 0;
  pointer-events: none;
  overflow: hidden;
}
.blob {
  position: absolute;
  border-radius: 50%;
  filter: blur(90px);
  opacity: 0.5;
  animation: float 18s ease-in-out infinite;
}
.blob-a { width: 560px; height: 560px; background: #ffb26b; top: -160px; left: -140px; }
.blob-b { width: 500px; height: 500px; background: #6ea8fe; bottom: -180px; right: 16%; animation-delay: -6s; }
.blob-c { width: 420px; height: 420px; background: #a78bfa; top: 30%; right: -160px; animation-delay: -12s; }
@keyframes float {
  0%, 100% { transform: translate3d(0, 0, 0) scale(1); }
  50% { transform: translate3d(24px, -36px, 0) scale(1.08); }
}
.grid-mask {
  position: absolute;
  inset: 0;
  background-image:
    linear-gradient(rgba(255, 255, 255, 0.7) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.7) 1px, transparent 1px);
  background-size: 56px 56px;
  -webkit-mask-image: radial-gradient(circle at 30% 30%, #000, transparent 72%);
  mask-image: radial-gradient(circle at 30% 30%, #000, transparent 72%);
  opacity: 0.6;
}

/* ================= 主体布局（整体放大） ================= */
.login-body {
  position: relative;
  z-index: 1;
  margin: auto;
  width: min(1280px, 94vw);
  min-height: 700px;
  display: grid;
  grid-template-columns: minmax(440px, 520px) 1fr;
  gap: 56px;
  padding: 40px 0;
  box-sizing: border-box;
}

/* ================= 登录卡片 ================= */
.login-form-box {
  display: flex;
  flex-direction: column;
  padding: 40px 48px 42px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.84);
  backdrop-filter: blur(20px) saturate(180%);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  box-shadow:
    0 28px 70px -24px rgba(31, 45, 84, 0.3),
    0 0 0 1px rgba(255, 255, 255, 0.7) inset;
}
.login-form-box.is-shake { animation: shake 0.5s; }
@keyframes shake {
  10%, 90% { transform: translateX(-2px); }
  20%, 80% { transform: translateX(4px); }
  30%, 50%, 70% { transform: translateX(-6px); }
  40%, 60% { transform: translateX(6px); }
}

/* 头部 */
.login-form-box-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 34px;
}
.logo { display: flex; align-items: center; gap: 12px; }
.logo-hrcloud { height: 38px; }
.logo-line { width: 1px; height: 22px; background: #e5e8ef; }
.logo-payroll-tax { height: 32px; }

.lang-switch-trigger {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 8px 16px;
  border-radius: 999px;
  font-size: 14px;
  color: #5b6478;
  background: rgba(255, 255, 255, 0.75);
  border: 1px solid #e8ebf2;
  cursor: pointer;
  transition: all 0.2s;
}
.lang-switch-trigger:hover {
  color: #ff6b35;
  border-color: #ffd0b8;
  background: #fff;
}

/* 标题 */
.login-form-title {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1a2233;
  letter-spacing: 0.5px;
}
.login-form-subtitle {
  margin: 10px 0 30px;
  font-size: 14px;
  color: #8a92a6;
}

/* ================= 表单 ================= */
.field { margin-bottom: 24px; }
.field ::v-deep .el-input__inner {
  height: 52px;
  line-height: 52px;
  border-radius: 14px;
  border-color: #e6e9f0;
  background: #fff;
  font-size: 15px;
  padding-left: 42px;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.field ::v-deep .el-input__inner:hover { border-color: #cdd6e6; }
.field ::v-deep .el-input__inner:focus {
  border-color: #ff8a4c;
  box-shadow: 0 0 0 3px rgba(255, 122, 60, 0.12);
}
.field ::v-deep .el-input__prefix { left: 14px; color: #a3abbd; font-size: 18px; }
.field.is-error ::v-deep .el-input__inner { border-color: #f56c6c; }
.field ::v-deep .el-form-item__error { padding-top: 5px; font-size: 12px; }

/* 密码可见切换 */
.pwd-toggle {
  position: relative;
  font-size: 18px;
  color: #a3abbd;
  cursor: pointer;
  margin-right: 6px;
  transition: color 0.2s;
}
.pwd-toggle:hover { color: #ff8a4c; }
.pwd-toggle.is-off::after {
  content: '';
  position: absolute;
  left: -2px;
  right: -2px;
  top: 50%;
  height: 1px;
  background: currentColor;
  transform: rotate(-45deg);
}

/* 验证码行 */
.code-row { display: flex; align-items: center; gap: 14px; }
.code-row .el-input { flex: 1; }
.code-img {
  flex: none;
  width: auto;
  height: 52px;
  border-radius: 14px;
  overflow: hidden;
  border: 1px solid #e6e9f0;
  background: #f7f9fc;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;
}
.code-img:hover { border-color: #ffd0b8; box-shadow: 0 4px 12px -6px rgba(255, 122, 60, 0.6); }
.code-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
.code-img.is-loading img { opacity: 0.4; }
.code-img i { position: absolute; color: #ff6b35; font-size: 18px; }

/* 记住账号 / 链接 */
.login-options {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 4px 0 26px;
}
.login-options ::v-deep .el-checkbox__label { font-size: 13px; color: #8a92a6; }
.links { display: flex; align-items: center; gap: 12px; font-size: 13px; }
.links a { color: #5b6478; cursor: pointer; transition: color 0.2s; }
.links a:hover { color: #ff6b35; }
.links .divider { width: 1px; height: 11px; background: #e0e4ec; }

/* 登录按钮 */
.login-submit {
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 14px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 1px;
  color: #fff;
  background: linear-gradient(135deg, #ffa04d 0%, #ff6b35 100%);
  box-shadow: 0 14px 28px -14px rgba(255, 107, 53, 0.9);
  transition: transform 0.15s, box-shadow 0.2s, filter 0.2s;
}
.login-submit:hover {
  color: #fff;
  background: linear-gradient(135deg, #ffa04d 0%, #ff6b35 100%);
  filter: brightness(1.04);
  transform: translateY(-1px);
  box-shadow: 0 18px 32px -14px rgba(255, 107, 53, 0.95);
}
.login-submit:active { transform: translateY(0); }

/* 协议 */
.agreement {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-top: 22px;
  font-size: 13px;
  color: #8a92a6;
}
.agreement ::v-deep .el-checkbox__label { display: none; }
.agreement ::v-deep .el-checkbox__inner { border-radius: 4px; }
.policy-link { color: #ff6b35; text-decoration: none; }
.policy-link:hover { text-decoration: underline; }

/* ================= 选择企业 ================= */
.enterprise-panel { display: flex; flex-direction: column; min-height: 460px; }
.enterprise-list {
  list-style: none;
  margin: 0;
  padding: 2px;
  display: flex;
  flex-direction: column;
  gap: 14px;
  max-height: 380px;
  overflow-y: auto;
}
.enterprise-item {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px 20px;
  border-radius: 16px;
  background: #fff;
  border: 1px solid #eef1f7;
  cursor: pointer;
  transition: all 0.2s;
  animation: itemIn 0.35s ease both;
}
.enterprise-item:hover {
  border-color: #ffd0b8;
  box-shadow: 0 14px 28px -18px rgba(255, 107, 53, 0.9);
  transform: translateY(-2px);
}
.enterprise-item i { color: #c3cad9; transition: color 0.2s, transform 0.2s; }
.enterprise-item:hover i { color: #ff6b35; transform: translateX(2px); }
.enterprise-avatar {
  flex: none;
  width: 42px;
  height: 42px;
  border-radius: 12px;
  display: grid;
  place-items: center;
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  background: linear-gradient(135deg, #ffb26b, #ff6b35);
}
.enterprise-name {
  flex: 1;
  min-width: 0;
  font-size: 15px;
  color: #2a3245;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
@keyframes itemIn {
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
}
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: auto;
  padding-top: 24px;
  font-size: 14px;
  color: #8a92a6;
  cursor: pointer;
  align-self: center;
  transition: color 0.2s;
}
.back-link:hover { color: #ff6b35; }

/* ================= 版本号 ================= */
.login-version {
  height: 22px;
  display: flex;
  justify-content: center;
  margin-top: 22px;
}
.version-tag {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  height: auto;
  padding: 5px 14px 5px 12px;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.6px;
  color: #8a92a6;
  background: linear-gradient(135deg, #ffffff 0%, #f7f9fc 100%);
  border: 1px solid #e8ebf2;
  border-radius: 999px;
  box-shadow: 0 2px 8px -4px rgba(31, 45, 84, 0.15);
  cursor: default;
  user-select: none;
  transition: all 0.28s cubic-bezier(0.4, 0, 0.2, 1);
}
/* 覆盖 el-tag 内部默认颜色 */
.version-tag ::v-deep span,
.version-tag ::v-deep .el-tag__content {
  color: inherit;
  display: inline-flex;
  align-items: center;
  gap: 7px;
}
.version-tag:hover {
  color: #ff6b35;
  border-color: #ffd0b8;
  background: linear-gradient(135deg, #fff 0%, #fff5ef 100%);
  box-shadow: 0 6px 16px -6px rgba(255, 107, 53, 0.45);
  transform: translateY(-1px);
}
.version-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: linear-gradient(135deg, #ffb26b 0%, #ff6b35 100%);
  box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.12);
  animation: versionPulse 2.4s ease-in-out infinite;
}
@keyframes versionPulse {
  0%, 100% { box-shadow: 0 0 0 3px rgba(255, 107, 53, 0.12); }
  50%      { box-shadow: 0 0 0 5px rgba(255, 107, 53, 0.06); }
}
.version-text {
  font-family: 'SF Mono', 'Menlo', 'Consolas', monospace;
  font-variant-numeric: tabular-nums;
}

/* ================= 右侧品牌区（同步放大） ================= */
.login-desc-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 28px;
  padding: 56px 32px;
  border-radius: 28px;
  background: linear-gradient(160deg, rgba(255, 255, 255, 0.55), rgba(255, 255, 255, 0.15));
  box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.5) inset;
}
.login-desc-main {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 26px;
  width: 100%;
}
/* 系统名 + 版本号 同行 */
.sys-name-row {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
}
.sys-name {
  height: 80px;
  flex: none;
}
.sys-name ::v-deep .el-image {
  height: 100%;
  width: auto;
}
.payroll-text { width: 84%; }
.about-us { width: 190px; display: block; transition: opacity 0.2s; }
.about-us:hover { opacity: 0.8; }
.f-copyright { font-size: 13px; color: #93a0b8; text-align: center; }

/* ================= 切换过渡 ================= */
.view-fade-enter-active,
.view-fade-leave-active { transition: opacity 0.25s ease, transform 0.25s ease; }
.view-fade-enter { opacity: 0; transform: translateY(10px); }
.view-fade-leave-to { opacity: 0; transform: translateY(-10px); }

/* ================= 响应式 ================= */
@media (max-width: 1100px) {
  .login-body {
    width: min(1080px, 94vw);
    grid-template-columns: minmax(400px, 480px) 1fr;
    gap: 36px;
  }
  .login-form-box { padding: 34px 36px 36px; }
  .login-desc-box { padding: 40px 24px; }
  .sys-name { height: 68px; }
  .payroll-text { width: 78%; }
  .about-us { width: 160px; }
}
@media (max-width: 960px) {
  .login-body {
    grid-template-columns: 1fr;
    width: min(540px, 94vw);
    min-height: auto;
    padding: 36px 0;
  }
  .login-desc-box { display: none; }
  .login-form-box { padding: 32px 28px; }
  .enterprise-panel { min-height: 340px; }
}
@media (max-width: 480px) {
  .login-form-box { border-radius: 22px; padding: 28px 20px; }
  .login-form-title { font-size: 24px; }
  .login-form-box-header { margin-bottom: 26px; }
  .logo-hrcloud { height: 32px; }
  .logo-payroll-tax { height: 26px; }
  .field ::v-deep .el-input__inner { height: 48px; line-height: 48px; }
  .code-img { width: 104px; height: 48px; }
  .login-submit { height: 48px; }
}

/* suffix 垂直居中，修复小眼睛偏移 */
.field ::v-deep .el-input__suffix {
  display: flex;
  align-items: center;
  justify-content: center;
}
.field ::v-deep .el-input__suffix-inner {
  display: inline-flex;
  align-items: center;
}
.pwd-toggle {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  font-size: 18px;
  color: #a3abbd;
  cursor: pointer;
  margin-right: 6px;
  transition: color 0.2s;
}
.login-version {
  display: flex;
  justify-content: center;
  margin-top: 2px;
}
</style>
